// frontend/src/components/TermsModal.js
import React, { useState, useEffect } from "react";
// Use the left-aligned CSS (terms-modal-css-left-align)
import "../styles/TermsModal.css";

// isMandatory prop is still used for overlay click logic
const TermsModal = ({ isOpen, onAccept, onClose, isMandatory }) => {
  // Checkbox state
  const [isChecked, setIsChecked] = useState(false);
  useEffect(() => {
    if (isOpen) {
      setIsChecked(false); // Reset checkbox when modal opens
    }
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  // Handler for the Accept button
  const handleAccept = () => {
    if (isChecked) {
      onAccept(); // Call the function passed from LoginPage (saves to localStorage, etc.)
    }
  };

  // Handler for clicking the overlay background
  const handleOverlayClick = () => {
    // Only allow closing via overlay if it's NOT mandatory
    if (!isMandatory) {
      onClose();
    }
  };

  // NOTE: Legal review of the text content is still recommended,
  // especially the incomplete sentence in Section 10.

  return (
    // Pass overlay click handler
    <div className="modal-overlay" onClick={handleOverlayClick}>
      {/* Stop propagation prevents closing when clicking inside content */}
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        {/* Using h2 for the main title */}
        <h2>Terms of Service for Cloudfresh License Manager</h2>
        {/* Scrollable Terms Text Area */}
        <div className="terms-text-area">
          {/* ----- LATEST TERMS OF SERVICE TEXT START ----- */}
          <p>
            <strong>Effective Date:</strong> April 2, 2025
          </p>

          <h3>1. Introduction and Agreement</h3>
          <p>
            Please read these Terms of Service ("Terms", "Terms of Service")
            carefully before using the Cloudfresh License Manager application
            (the "Service", "Application") operated by Cloudfresh ("us", "we",
            or "our").
          </p>
          <p>
            Your access to and use of the Service is conditioned upon your
            acceptance of and compliance with these Terms.
            <br />
            These Terms apply to all visitors, users, including administrators
            and end-users designated by your organization and others who wish to
            access or use the Service.
          </p>
          <p>
            <strong>
              By accessing or using the Service, you signify your agreement to
              be bound by these Terms. If you disagree with any part of the
              terms, you do not have permission to access and must not use the
              Service.
            </strong>
          </p>

          <h3>2. Description of Service</h3>
          <p>
            Cloudfresh License Manager is a software tool provided by Cloudfresh
            designed to assist authorized administrators and users in managing
            Google Workspace licenses and subscriptions procured through
            Cloudfresh or its associated reseller activities. The Service
            provides functionalities such as viewing subscription details,
            tracking license seat usage, facilitating the addition of seats to
            eligible subscriptions{" "}
            <strong>
              subject to limits defined by administrators or subscription terms
            </strong>
            . The Service functions primarily as an interface to interact with
            Google's Reseller APIs and related Google Workspace services.
          </p>

          <h3>3. Relationship with Google Workspace Terms</h3>
          <p>You acknowledge and agree that:</p>
          <ul>
            <li>
              The Service is an auxiliary tool for managing aspects of your
              Google Workspace environment.
            </li>
            <li>
              Your use of Google Workspace itself, including all licenses
              managed through this Service, remains governed by the applicable
              Google Workspace agreement(s) between your organization and Google
              or the authorized reseller.
            </li>
            <li>
              Cloudfresh is not a party in your Google Workspace agreement with
              Google. These Terms govern only for your use of the Cloudfresh
              License Manager tool itself.
            </li>
            <li>
              You are responsible for understanding and complying with all
              applicable Google terms. For reference, information regarding
              Google Workspace agreements can be found via Google's support
              resources. An example of such a resource is:
              <br />
              <a
                href="https://support.google.com/a/answer/33387"
                target="_blank"
                rel="noopener noreferrer"
              >
                https://support.google.com/a/answer/33387
              </a>
            </li>
            <li>
              <em>
                (Note: This link is provided for informational purposes. You are
                responsible for identifying and reviewing the specific Google
                terms applicable to your organization's agreement.)
              </em>
            </li>
          </ul>

          <h3>4. Eligibility and User Accounts</h3>
          <ul>
            <li>
              Access to the Service is limited to authorized users belonging to
              organizations that have procured Google Workspace licenses through
              Cloudfresh or its partners.
            </li>
            <li>
              Authentication is required using valid Google Workspace account
              credentials.
            </li>
            <li>
              You are responsible for maintaining the security and
              confidentiality of your Google Workspace account credentials. You
              must notify your organization administrator and/or Cloudfresh
              immediately of any suspected unauthorized use or security breach.
            </li>
          </ul>

          <h3>5. User Responsibilities and Acceptable Use</h3>
          <ul>
            <li>
              You agree to use the Service solely for its intended purpose –
              managing your organization's Google Workspace licenses – as
              authorized by your organization and in compliance with these
              Terms, applicable laws, and Google's terms.
            </li>
            <li>
              You are solely responsible for all activities conducted through
              your account within the Service.
            </li>
            <li>
              <strong>Cost Responsibility:</strong> You acknowledge that actions
              taken within the Service, particularly adding license seats, may
              incur additional financial obligations for your organization,
              payable to Google or the relevant reseller according to your
              Google Workspace agreement and pricing. Cloudfresh is not liable
              for any costs incurred as a result of using the Service
              functionalities.
            </li>
            <li>
              You agree not to:
              <ul>
                <li>
                  Misuse the service or use it for any illegal or unauthorized
                  purpose.
                </li>
                <li>
                  Attempt to circumvent security measures or access controls.
                </li>
                <li>
                  Interfere with the proper functioning of the service, its
                  infrastructure, or Google's APIs.
                </li>
                <li>
                  Resell, sublicense, or otherwise commercially use the service
                  itself.
                </li>
                <li>
                  Exceed any usage limitations or restrictions imposed by your
                  administrator or the Service.
                </li>
              </ul>
            </li>
          </ul>

          <h3>6. Intellectual Property Rights</h3>
          <p>
            The service, including its software, design, text, graphics, logos,
            underlying code, features, and functionality (excluding data
            retrieved from Google APIs or data belonging to your organization),
            is and remains the exclusive intellectual property of Cloudfresh and
            its licensors. The Service is protected by copyright, trademark, and
            other intellectual property laws of Ukraine and other countries. You
            may not copy, modify, distribute, sell, or lease any part of our
            Service or included software, nor may you reverse engineer or
            attempt to extract the source code of that software, unless laws
            prohibit those restrictions or you have our written permission.
          </p>

          <h3>7. License Grant</h3>
          <p>
            Subject to your compliance with these terms, Cloudfresh grants you a
            limited, non-exclusive, non-transferable, non-sublicensable,
            revocable license to access and use the Service during the term of
            your organization's active relationship with Cloudfresh for the
            relevant Google Workspace services, solely for your internal
            business purposes related to managing those services.
          </p>

          <h3>8. Data Privacy</h3>
          <p>
            Our collection, use, and protection of personal data in connection
            with your use of the service are governed by our Privacy Policy{" "}
            <a
              href="https://cloudfresh.com/en/privacy-policy/"
              target="_blank"
              rel="noopener noreferrer"
            >
              https://cloudfresh.com/en/privacy-policy/
            </a>
            . By using the Service, you consent to the data practices described
            in the Privacy Policy. You and your organization are responsible for
            ensuring that your use of the Service complies with all applicable
            data protection laws (like GDPR, etc.) regarding the user and
            license data being managed.
          </p>

          <h3>9. Disclaimers of Warranties</h3>
          <p>
            The service is provided "as is" and "as available," without warranty
            of any kind, either express or implied. To the fullest extent
            permissible under applicable law, Cloudfresh and its affiliates,
            licensors, and suppliers disclaim all warranties, express or
            implied, including, but not limited to, implied warranties of
            merchantability, fitness for a particular purpose, title, and
            non-infringement.
          </p>
          <p>
            Cloudfresh does not warrant that the service will be uninterrupted,
            error-free, secure, or free of viruses or other harmful components.
            Cloudfresh does not warrant that the data or results obtained
            through the service (which rely heavily on google's apis) will be
            accurate, complete, or reliable. You may use the service
            acknowledging this risk.
          </p>

          <h3>10. Limitation of Liability</h3>
          <p>
            To the fullest extent permitted by applicable law, in no event shall
            Cloudfresh, its affiliates, directors, employees, agents, suppliers,
            or licensors be liable for any indirect, incidental, special,
            consequential, exemplary, or punitive damages. Including but not
            limited to, damages for loss of profits, revenue, goodwill, use,
            data, or other intangible losses, arising out of or relating to your
            access to or use of, or your inability to access or use, the
            service, whether based on warranty, contract, tort (including
            negligence), statute, or any other legal theory, even if Cloudfresh
            has been advised of the possibility of such damages.
          </p>
          <p>
            In no event shall Cloudfresh total aggregate liability arising out
            of or related to these terms or the service exceed
            {/* NOTE: The text provided ended here. Legal review is needed. */}
          </p>

          <h3>11. Indemnification</h3>
          <p>
            You agree to indemnify, defend, and hold harmless Cloudfresh and its
            officers, directors, employees, agents, affiliates, successors, and
            assigns from and against any and all losses, damages, liabilities,
            deficiencies, claims, actions, judgments, settlements, interest,
            awards, penalties, fines, costs, or expenses of whatever kind,
            including reasonable attorneys fees, arising from or relating to
            your use or misuse of the Service or your breach of these terms.
          </p>

          <h3>12. Termination</h3>
          <p>
            We may terminate or suspend your access to the service immediately,
            without prior notice or liability, for any reason, including,
            without limitation, if you breach these terms. Your organization
            administrator may also terminate your access. Upon termination, your
            right to use the service will cease immediately. Provisions of these
            terms that by their nature should survive termination shall survive
            (including, e.g., Intellectual Property, Disclaimers, Limitation of
            Liability, Indemnification, Governing Law).
          </p>

          <h3>13. Governing Law and Dispute Resolution</h3>
          <p>
            These Terms shall be governed by and construed in accordance with
            the laws of Ukraine, without regard to its conflict of law
            principles. Any disputes arising out of or relating to these Terms
            or the Service shall be resolved exclusively in the competent courts
            located in Kyiv, Ukraine.
          </p>

          <h3>14. Changes to Terms</h3>
          <p>
            Cloudfresh reserves the right, at its sole discretion, to modify or
            replace these terms at any time. If a revision is material (as
            determined in our sole discretion), we will make reasonable efforts
            to provide at least 15 days notice prior to the new terms taking
            effect, potentially via the service interface or by notifying your
            organization administrator. By continuing to access or use the
            service after those revisions become effective, you agree to be
            bound by the revised terms.
          </p>

          <h3>15. Contact Information</h3>
          <p>
            If you have any questions about these Terms, please contact
            Cloudfresh at:{" "}
            <a href="mailto:s@cloudfresh.com">s@cloudfresh.com</a>.
          </p>
          {/* ----- LATEST TERMS OF SERVICE TEXT END ----- */}
        </div>{" "}
        {/* End of terms-text-area */}
        {/* --- Footer Section (Checkbox & Accept Button Always Visible) --- */}
        <div className="terms-agreement">
          <input
            type="checkbox"
            id="termsCheckbox"
            checked={isChecked}
            onChange={(e) => setIsChecked(e.target.checked)}
            aria-labelledby="termsCheckboxLabel"
          />
          <label htmlFor="termsCheckbox" id="termsCheckboxLabel">
            I have read and agree to the Terms of Service.
          </label>
        </div>
        <div className="modal-actions">
          <button
            onClick={handleAccept}
            disabled={!isChecked}
            className={`modal-button accept-button ${
              !isChecked ? "disabled" : ""
            }`}
            aria-disabled={!isChecked}
          >
            Accept
          </button>
        </div>
        {/* --- End Footer Section --- */}
      </div>{" "}
      {/* End of modal-content */}
    </div> // End of modal-overlay
  );
};

export default TermsModal;
